/**
 * Creates a cache key for a visualization group based on its associated slot selections.
 * @param {string} groupId - The visualization group ID (or 'legacy' for legacy mode)
 * @param {Array<{id: string, selected: any, groupLineItemPropertyKey?: string|null}>} groupSlots - The slots associated with this group
 * @param {string} productVariantId - The selected product variant ID
 * @returns {string} A cache key string
 */
export const createGroupCacheKey = (groupId, groupSlots, productVariantId) => {
  // Create a stable key based on slot IDs and their selected values
  // For File objects, we use a combination of name, size, and lastModified
  const slotKey = groupSlots
    .map((slot) => {
      const selected = slot.selected;
      // Slot-group (e.g. engraving colour option) slots share the same file;
      // the active-group marker is what changes when the customer switches
      // option, so it must be part of the key or the switch serves a stale
      // render in the old colour.
      const activeMarker = slot.groupLineItemPropertyKey ? `:active:${slot.groupLineItemPropertyKey}` : '';

      if (selected instanceof File) {
        // For files, use metadata that identifies the file uniquely
        return `${slot.id}:file:${selected.name}:${selected.size}:${selected.lastModified}${activeMarker}`;
      } else if (selected && typeof selected === 'object') {
        // For objects (like text selections), stringify them
        return `${slot.id}:${JSON.stringify(selected)}${activeMarker}`;
      } else {
        // For primitives, use the value directly
        return `${slot.id}:${selected}${activeMarker}`;
      }
    })
    .sort()
    .join('|');

  return `${groupId}:${productVariantId}:${slotKey}`;
};
